import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  GHADiscountExclusionBody,
  GHADiscountExclusionPatchBody,
  ListGHAScopedRulesFilters,
  createGHADiscountExclusion,
  listGHADiscountExclusions,
  updateGHADiscountExclusion,
} from '~/services/ConnectionGoogleService';

import { invalidateGHADiscountSummary } from './useGHADiscountSummary';

export const ghaExclusionQueryKeys = {
  all: ['ghaDiscountExclusions'] as const,
  lists: () => [...ghaExclusionQueryKeys.all, 'list'] as const,
  list: (filters: ListGHAScopedRulesFilters) => [...ghaExclusionQueryKeys.lists(), filters] as const,
};

export function useGHADiscountExclusions(filters: ListGHAScopedRulesFilters = {}) {
  return useQuery({
    queryKey: ghaExclusionQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHADiscountExclusions(filters, { signal });
      return { result: response.result, total: response.total };
    },
    // Filter/toggle changes keep the previous rows visible while refetching (same pattern as
    // Pull with Hints / property management) instead of flashing the whole panel to a spinner.
    placeholderData: keepPreviousData,
  });
}

export function useCreateGHADiscountExclusion() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (input: GHADiscountExclusionBody) => await createGHADiscountExclusion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaExclusionQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar('Exclusion created', { variant: 'success' });
    },
  });
}

export function useUpdateGHADiscountExclusion() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: GHADiscountExclusionPatchBody }) =>
      await updateGHADiscountExclusion(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ghaExclusionQueryKeys.lists() });
      invalidateGHADiscountSummary(queryClient);
      enqueueSnackbar('Exclusion updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update exclusion: ${error.message}`, { variant: 'error' });
    },
  });
}
